// Room camera: wheel-zoom (anchored on cursor) + click-drag pan + eased
// fly-to-center on selection. Page stays fixed; only the room view moves.
import { useCallback, useEffect, useRef, useState } from 'react';

export interface Cam {
  scale: number;
  ox: number; // canvas top-left offset in stage CSS px
  oy: number;
}

export function fitCam(stageW: number, stageH: number, worldW: number, worldH: number): Cam {
  const scale = Math.min(stageW / worldW, stageH / worldH);
  return {
    scale,
    ox: Math.max(0, (stageW - worldW * scale) / 2),
    oy: Math.max(0, (stageH - worldH * scale) / 2),
  };
}

/** Cover the stage with the brown room: full width, bottom flush with the screen. */
export function fitRoomFill(
  stageW: number,
  stageH: number,
  roomSize: number,
  padX: number,
  padTop: number,
): Cam {
  const scale = Math.max(stageW / roomSize, stageH / roomSize);
  return {
    scale,
    ox: -padX * scale,
    oy: stageH - (padTop + roomSize) * scale,
  };
}

export function zoomAt(
  cam: Cam,
  px: number, // cursor in stage coords
  py: number,
  deltaY: number,
  min: number,
  max: number,
): Cam {
  const ns = Math.min(max, Math.max(min, cam.scale * Math.exp(-deltaY * 0.0015)));
  const k = ns / cam.scale;
  return { scale: ns, ox: px - (px - cam.ox) * k, oy: py - (py - cam.oy) * k };
}

// Camera target that centers a world point in the stage view — both axes.
// The canvas overhangs the room (padding), so the world's top pad offset
// must be subtracted or the person lands bottom-middle-ish.
export function camCenteringOn(
  cam: Cam,
  stageW: number,
  stageH: number,
  worldX: number,
  worldY: number,
  canvasW: number,
  canvasH: number,
  worldPadX = 0,
  worldPadTop = 0,
): Cam {
  // Canvas is CSS-sized at world*scale, positioned at (ox, oy). Person's
  // position ON the canvas includes the room offset; center their exact px.
  const cw = canvasW * cam.scale;
  const ch = canvasH * cam.scale;
  const ox = stageW / 2 - (worldX + worldPadX) * cam.scale;
  const oy = stageH / 2 - (worldY + worldPadTop) * cam.scale;
  // Clamp so the canvas stays inside the stage when it's smaller, or covers
  // the stage fully when bigger (no empty beyond-edges gutters).
  const clamp = (v: number, canvasSize: number, stageSize: number): number =>
    canvasSize <= stageSize
      ? Math.min(Math.max(v, 0), stageSize - canvasSize) // fully visible, centered-ish
      : Math.min(Math.max(v, stageSize - canvasSize), 0); // covers, no gutter
  return { scale: cam.scale, ox: clamp(ox, cw, stageW), oy: clamp(oy, ch, stageH) };
}

export function useCamera(): {
  cam: Cam;
  setCam: (c: Cam) => void;
  fit: (stageW: number, stageH: number, worldW: number, worldH: number) => number;
  minScale: React.MutableRefObject<number>;
  beginDrag: (sx: number, sy: number) => void;
  dragTo: (sx: number, sy: number) => void;
  endDrag: () => boolean; // true = it was a click (barely moved)
  dragging: boolean;
  flyTo: (
    stageW: number,
    stageH: number,
    worldX: number,
    worldY: number,
    canvasW: number,
    canvasH: number,
    worldPadX?: number,
    worldPadTop?: number,
  ) => void;
  cancelFly: () => void;
} {
  const [cam, setCamState] = useState<Cam>({ scale: 1, ox: 0, oy: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: number } | null>(null);
  const camRef = useRef(cam);
  camRef.current = cam;
  const minScale = useRef(0.1);

  const setCam = useCallback((c: Cam) => {
    camRef.current = c;
    setCamState(c);
  }, []);

  // Fly-to-center: ~350ms ease-out animation from current cam to target.
  const flyRef = useRef<{ from: Cam; to: Cam; start: number; dur: number } | null>(null);

  const fit = useCallback(
    (stageW: number, stageH: number, worldW: number, worldH: number): number => {
      const c = fitCam(stageW, stageH, worldW, worldH);
      minScale.current = c.scale * 0.4;
      setCam(c);
      return c.scale;
    },
    [setCam],
  );

  const beginDrag = useCallback((sx: number, sy: number) => {
    dragRef.current = { sx, sy, ox: camRef.current.ox, oy: camRef.current.oy, moved: 0 };
    setDragging(true);
    flyRef.current = null; // manual pan cancels any in-flight glide
  }, []);

  const dragTo = useCallback(
    (sx: number, sy: number) => {
      const d = dragRef.current;
      if (!d) return;
      d.moved = Math.max(d.moved, Math.hypot(sx - d.sx, sy - d.sy));
      setCam({ ...camRef.current, ox: d.ox + (sx - d.sx), oy: d.oy + (sy - d.sy) });
    },
    [setCam],
  );

  const endDrag = useCallback((): boolean => {
    const d = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    return (d?.moved ?? 99) < 6; // click vs pan threshold (screen px)
  }, []);

  // Fly-to-center: eased animation from current cam to target (flyRef above).
  // worldPad* compensates the canvas overhang so the point truly centers.
  const flyTo = useCallback(
    (
      stageW: number,
      stageH: number,
      worldX: number,
      worldY: number,
      canvasW: number,
      canvasH: number,
      worldPadX = 0,
      worldPadTop = 0,
    ): void => {
      const to = camCenteringOn(camRef.current, stageW, stageH, worldX, worldY, canvasW, canvasH, worldPadX, worldPadTop);
      flyRef.current = { from: camRef.current, to, start: performance.now(), dur: 350 };
    },
    [],
  );
  const cancelFly = useCallback((): void => {
    flyRef.current = null;
  }, []);

  // Per-frame fly easing; driven by the game loop's rAF, so no extra timer.
  useEffect(() => {
    let raf = 0;
    const step = (t: number): void => {
      raf = requestAnimationFrame(step);
      const f = flyRef.current;
      if (!f) return;
      const k = Math.min(1, (t - f.start) / f.dur);
      const e = 1 - Math.pow(1 - k, 3); // ease-out cubic
      setCam({
        scale: f.from.scale + (f.to.scale - f.from.scale) * e,
        ox: f.from.ox + (f.to.ox - f.from.ox) * e,
        oy: f.from.oy + (f.to.oy - f.from.oy) * e,
      });
      if (k >= 1) flyRef.current = null;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [setCam]);

  return { cam, setCam, fit, minScale, beginDrag, dragTo, endDrag, dragging, flyTo, cancelFly };
}

// Fit the stage element around a world rect (shared by auto-fit + Fit button).
export function fitStageToWorld(
  fit: (stageW: number, stageH: number, worldW: number, worldH: number) => number,
  stage: { getBoundingClientRect: () => { width: number; height: number } } | null,
  worldW: number,
  worldH: number,
): void {
  if (!stage) return;
  const r = stage.getBoundingClientRect();
  fit(r.width, r.height, worldW, worldH);
}
